<template>
    <div style="background: transparent" class="d-flex flex-d-c max-width p-relative input-simple-container">
        <ion-input
            class="input-simple light-grey4-background purple-purple-color"
            :class="inputClass"
            v-model="inputModelValue"
            :accept="props.accept"
            :autocapitalize="props.autocapitalize"
            :autocomplete="props.autocomplete"
            :autocorrect="props.autocorrect"
            :autofocus="props.autofocus"
            :clearOnEdit="props.clearOnEdit"
            :color="props.color"
            :debounce="props.debounce"
            :disabled="props.disabled"
            :enterkeyhint="props.enterkeyhint"
            :inputmode="props.inputmode"
            :max="props.max"
            :maxlength="props.maxlength"
            :min="props.min"
            :minlength="props.minlength"
            :mode="props.mode"
            :multiple="props.multiple"
            :pattern="props.pattern"
            :placeholder="props.placeholder"
            :readonly="props.readonly"
            :required="props.required"
            :size="props.size"
            :spellcheck="props.spellcheck"
            :step="props.step"
            :type="props.type"
            :name="name"
            @blur="handleBlur"
            @keydown.enter="$emit('submit')"
            @ionChange="emit('change', inputModelValue)"
        >
            <slot />
        </ion-input>

        <div
            class="p-absolute input-icon-container" v-if="$slots['icon']"
            :class="{'error-input-icon-container': errors.length}"
        >
            <slot name="icon" />
        </div>


        <div v-if="errors.length || $slots['error-message']" class="coral-grey3-color fs-small pt-1 pl-1">
            <slot name="error-message">
                <span v-for="error of errors" :key="error">{{ error }}</span>
            </slot>
        </div>


        <slot name="custom-helper">
            <div
                v-if="$slots['helper']" class="coral-grey3-color fs-small pt-1 pl-1"
                style="min-height: calc(15px + .5em);"
            >
                <slot name="helper" />
            </div>
        </slot>
    </div>
</template>

<script setup lang="ts">
import { IonInput } from "@ionic/vue";
import { computed, onMounted } from "vue";
import { useField } from "vee-validate";

import { AUTO_CAPITALIZE_MODE, AUTO_COMPLETE_FIELD, INPUT_TYPE } from "@/types/enum/ionicEnum";
import { BaseInputProps } from "@/types/props";
import { BaseInputEmits } from "@/types/emits";

//TODO: Implement a way to only give a value with the prop "value"
const props = withDefaults(defineProps<BaseInputProps>(), {
    autocapitalize: AUTO_CAPITALIZE_MODE.OFF,
    autocomplete: AUTO_COMPLETE_FIELD.OFF,
    autocorrect: 'off',
    autofocus: false,
    clearInput: false,
    debounce: 0,
    readonly: false,
    required: false,
    spellcheck: false,
    type: INPUT_TYPE.TEXT,
    labelWidth: 'auto'
})
const emit = defineEmits<BaseInputEmits>()

// Vee-Validate Composable
const {
    name,
    errors,
    handleBlur,
    validate,
} = useField(
    props.validateOption?.name ?? '',
    props.validateOption?.rules ?? '',
    {
        syncVModel: true,
        valueProp: props.modelValue,
        type: props.type,
        initialValue: JSON.parse(JSON.stringify(props.modelValue ?? '')),
        label: props.validateOption?.label ?? props.validateOption?.name,
        ...props.validateOption?.options ?? {}
    }
)

const inputModelValue = computed<string | number | undefined>({
    get: () => {
        return props.modelValue
    },
    set: (value) => {
        emit('update:modelValue', value);
    }
})

window.addEventListener('change-language', () => validate({ mode: 'force' }))

onMounted(() => {
    const root: HTMLElement | null = document.querySelector(':root')
    root?.style.setProperty('--input-label-width-input-custom', props.labelWidth)
})
</script>

<style scoped>
.input-simple-container {
    max-width: 500px;
}

.input-simple {
    border-radius: 8px;
    --border-radius: 8px;
    --placeholder-color: var(--ion-color-grey1);
    padding-left: 10px !important;
    --padding-start: 10px;
    --highlight-color-focused: transparent;
    min-height: 38px !important;
}

.input-simple.inherit-background::part(native) {
    background: inherit;
}
</style>

<style>
.input-simple > input[type=number] {
    -moz-appearance: textfield;
}

.input-simple > input::-webkit-outer-spin-button,
.input-simple > input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.input-simple .native-input {
    caret-color: var(--ion-color-purple) !important;
}

.input-icon-container {
    right: 20px;
    z-index: 2;
    top: 7px;
    height: 24px;
    width: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.input-icon-container.error-input-icon-container {
    bottom: calc(13px + .5em);
}

.input-simple .input-highlight {
    left: 4px;
    width: calc(100% - 8px);
}
</style>

<style>
.input-simple .native-input[type="number"]::-webkit-outer-spin-button,
.input-simple .native-input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.input-simple[type="number"] .native-input {
    background: red;
}

.input-simple .native-input[type="number"] {
    -moz-appearance: textfield;
}
</style>

<style lang="scss">
@use '@/theme/mixin.scss';

.input-simple-container.input-border > ion-input {
    @include mixin.light {
        border: 1px solid var(--ion-color-grey3);
        border-radius: 8px;
    }

    @include mixin.dark {
        border: none;
    }
}
</style>
